import React, { useEffect, useState } from "react";
import ninaMarketState from "../data/ninaMarketState";
import {
  getStockInitialLiveData,
  getStockSnapshot,
} from "../marketData/marketDataApi";
import "./LiveChartWorkspace.css";
import "./LiveTradingProfessionalLayout.css";
import AITradeChecklist from "./AITradeChecklist";
import QuickTradeTicket from "./QuickTradeTicket";
import LiveProfessionalChartWorkspace from "../chartWorkspace/LiveProfessionalChartWorkspace";
import DecisionEngine from "./DecisionEngine";
import LiveCoach from "./LiveCoach";
import LiveChartRead from "./LiveChartRead";
import NinaSmartFlowPanel from "./NinaSmartFlowPanel";
import RiskEngine, { PartialProfitStrategy } from "./RiskEngine";
import { analyzeNinaSmartFlow } from "../liveTrading/NinaSmartFlowEngine";
import { analyzeLiveNinaDecision } from "../liveTrading/NinaLiveDecisionEngine";
import { analyzeLiveRisk } from "../liveTrading/NinaLiveRiskEngine";

const LIVE_REFRESH_MS = 15000;
const DEFAULT_SYMBOL = "PLUG";

function LiveDecisionReporter({ decision, onChange }) {
  const decisionLabel = decision?.decision;
  const confidence = decision?.confidence;
  const risk = decision?.risk;
  const status = decision?.status;

  useEffect(() => {
    if (typeof onChange !== "function") return;
    onChange({ decision: decisionLabel, confidence, risk, status });
  }, [confidence, decisionLabel, onChange, risk, status]);

  useEffect(
    () => () => {
      if (typeof onChange === "function") onChange(null);
    },
    [onChange]
  );

  return null;
}


function LiveTradePlanReporter({
  symbol,
  currentPrice,
  decision,
  confidence,
  risk,
  status,
  blocked,
  quantity,
  entry,
  stop,
  target1,
  target2,
  target3,
  maximumAllowedLoss,
  actualMaximumLoss,
  riskPerShare,
  onChange,
}) {
  useEffect(() => {
    if (typeof onChange !== "function") return;
  

    onChange({
      symbol,
      currentPrice,
      decision,
      confidence,
      risk,
      status,
      blocked,
      quantity,
      entry,
      stop,
      targets: {
        target1,
        target2,
        target3,
      },
      maximumAllowedLoss,
      actualMaximumLoss,
      riskPerShare,
    });
  }, [
    actualMaximumLoss,
    blocked,
    confidence,
    currentPrice,
    decision,
    entry,
    maximumAllowedLoss,
    onChange,
    quantity,
    risk,
    riskPerShare,
    status,
    stop,
    symbol,
    target1,
    target2,
    target3,
  ]);

  return null;
}
function initialSymbol(scannerReview) {
  try {
    return (
      localStorage.getItem("aninaSelectedSymbol") ||
      scannerReview?.symbol ||
      DEFAULT_SYMBOL
    )
      .trim()
      .toUpperCase();
  } catch (error) {
    return (scannerReview?.symbol || DEFAULT_SYMBOL).trim().toUpperCase();
  }
}

function initialAccountSize() {
  try {
    return Number(localStorage.getItem("aninaAccountSize")) || 10000;
  } catch (_error) {
    return 10000;
  }
}

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function positiveFiniteNumber(value) {
  const normalized = finiteNumber(value);
  return normalized !== null && normalized > 0 ? normalized : null;
}

function firstFinite(...values) {
  for (const value of values) {
    const normalized = finiteNumber(value);
    if (normalized !== null) return normalized;
  }
  return null;
}

function timestampMillis(value) {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function freshestAuthenticatedPrice(latestTrade, minuteBar) {
  const candidates = [
    {
      price: positiveFiniteNumber(latestTrade?.p),
      timestamp: timestampMillis(latestTrade?.t),
      priority: 0,
    },
    {
      price: positiveFiniteNumber(minuteBar?.c),
      timestamp: timestampMillis(minuteBar?.t),
      priority: 1,
    },
  ].filter((candidate) => candidate.price !== null);

  if (!candidates.length) return null;

  return candidates.reduce((best, candidate) => {
    if (!best) return candidate;
    if (candidate.timestamp !== null && best.timestamp !== null) {
      return candidate.timestamp > best.timestamp ? candidate : best;
    }
    if (candidate.timestamp !== null && best.timestamp === null) return candidate;
    if (candidate.timestamp === null && best.timestamp !== null) return best;
    return candidate.priority < best.priority ? candidate : best;
  }, null).price;
}

function scannerSeedToSnapshot(seed) {
  const symbol = String(seed?.symbol || "").trim().toUpperCase();
  const currentPrice = finiteNumber(seed?.currentPrice);
  if (!symbol || currentPrice === null) return null;

  const updatedAt = seed?.updatedAt || null;
  const bid = finiteNumber(seed?.bid);
  const ask = finiteNumber(seed?.ask);
  const regularClose = finiteNumber(seed?.regularClose);
  const previousClose = finiteNumber(seed?.previousClose);
  const volume = finiteNumber(seed?.volume);
  const vwap = finiteNumber(seed?.vwap);

  return {
    symbol,
    latest_trade: { p: currentPrice, t: updatedAt },
    latest_quote: {
      bp: bid,
      ap: ask,
      t: updatedAt,
    },
    minute_bar: {
      c: currentPrice,
      v: volume,
      vw: vwap,
      t: updatedAt,
    },
    daily_bar: {
      c: regularClose,
      v: volume,
      vw: vwap,
      t: updatedAt,
    },
    previous_daily_bar: {
      c: previousClose,
      t: null,
    },
    source: {
      provider: seed?.provider || "Alpaca",
      feed: seed?.feed || "",
      quality_level: seed?.quality || "Authenticated scanner evidence",
      last_update_at: updatedAt,
      scanner_seed: true,
      is_fresh: false,
      nina_data_quality_notice:
        seed?.notice ||
        "Authenticated scanner handoff seed is shown while a fresh Live Trading snapshot loads. Trading remains blocked until the fresh snapshot arrives.",
    },
  };
}

function liveTradeFromSnapshot(snapshot, selectedSymbol, fallbackTrade) {
  const latestTrade = snapshot?.latest_trade || {};
  const latestQuote = snapshot?.latest_quote || {};
  const minuteBar = snapshot?.minute_bar || {};
  const dailyBar = snapshot?.daily_bar || {};
  const previousDailyBar = snapshot?.previous_daily_bar || {};

  const currentPrice = freshestAuthenticatedPrice(latestTrade, minuteBar);
  const ask = positiveFiniteNumber(latestQuote.ap);
  const bid = positiveFiniteNumber(latestQuote.bp);
  const entry = firstFinite(ask, currentPrice);
  const safeEntry = entry !== null && entry > 0 ? entry : null;

  return {
    ...fallbackTrade,
    symbol: selectedSymbol,
    currentPrice,
    dayHigh: finiteNumber(dailyBar.h),
    dayLow: finiteNumber(dailyBar.l),
    openPrice: finiteNumber(dailyBar.o),
    previousClose: finiteNumber(previousDailyBar.c),
    bid,
    ask,
    volume: finiteNumber(dailyBar.v),
    vwap: firstFinite(dailyBar.vw, minuteBar.vw),
    ema20: null,
    entry: safeEntry,
    stop: safeEntry !== null ? Math.max(0.01, safeEntry - 0.1) : null,
    targets: {
      target1: safeEntry !== null ? safeEntry + 0.2 : null,
      target2: safeEntry !== null ? safeEntry + 0.4 : null,
      target3: safeEntry !== null ? safeEntry + 0.6 : null,
    },
    riskReward: safeEntry !== null ? "Planning levels" : "Unavailable",
    tradeStatus:
      currentPrice !== null
        ? "Authenticated market data connected"
        : "Authenticated market price unavailable",
    confirmationRequired: true,
  };
}

function LiveChartWorkspace({
  demoMarketState,
  scannerReview = null,
  scannerSeed = null,
  isActive = true,
  onLiveDecisionChange = null,
  onTradePlanChange = null,
  readOnlyMode = false,
}) {

  const [selectedSymbol, setSelectedSymbol] = useState(() => initialSymbol(scannerReview));
  const [liveAccountSize, setLiveAccountSize] = useState(initialAccountSize);
  const [tradingCardOpen, setTradingCardOpen] = useState(false);
  const [plannedQuantityState, setPlannedQuantityState] = useState({
    symbol: null,
    quantity: null,
  });
  const [liveSnapshot, setLiveSnapshot] = useState({
    symbol: initialSymbol(scannerReview),
    status: "loading",
    data: null,
    bundledBars: null,
    error: "",
  });

  const isDemoActive = Boolean(demoMarketState);
  const isScannerReview = Boolean(scannerReview);

  useEffect(() => {
    const handleAccountSizeChange = (event) => {
      const nextAccountSize = Number(event.detail?.accountSize);
      if (Number.isFinite(nextAccountSize)) {
        setLiveAccountSize(nextAccountSize);
      }
    };

    window.addEventListener("nina:account-size-change", handleAccountSizeChange);
    return () =>
      window.removeEventListener(
        "nina:account-size-change",
        handleAccountSizeChange
      );
  }, []);

  useEffect(() => {
    const handleSymbolChange = (event) => {
      const nextSymbol = String(event.detail?.symbol || "").trim().toUpperCase();
      if (!nextSymbol) return;
      setLiveSnapshot({
        symbol: nextSymbol,
        status: "loading",
        data: null,
        bundledBars: null,
        error: "",
      });
      setSelectedSymbol(nextSymbol);
    };

    window.addEventListener("nina:symbol-change", handleSymbolChange);
    return () => window.removeEventListener("nina:symbol-change", handleSymbolChange);
  }, []);

  useEffect(() => {
    if (isDemoActive || isScannerReview) return;
    const seedSnapshot = scannerSeedToSnapshot(scannerSeed);
    if (!seedSnapshot || seedSnapshot.symbol !== selectedSymbol) return;
    setLiveSnapshot({
      symbol: selectedSymbol,
      status: "seeded",
      data: seedSnapshot,
      bundledBars: null,
      error: "",
    });
  }, [isDemoActive, isScannerReview, scannerSeed, selectedSymbol]);

  useEffect(() => {
    if (isDemoActive || isScannerReview || !isActive) return undefined;

    let active = true;
    let timerId = null;
    const hasMatchingSeed =
      String(scannerSeed?.symbol || "").trim().toUpperCase() === selectedSymbol &&
      finiteNumber(scannerSeed?.currentPrice) !== null;

    const loadSnapshot = async ({ preserveCurrent = false, includeBundle = false } = {}) => {
      if (!preserveCurrent) {
        setLiveSnapshot({
          symbol: selectedSymbol,
          status: "loading",
          data: null,
          bundledBars: null,
          error: "",
        });
      }

      try {
        const result = includeBundle
          ? await (readOnlyMode ? getStockInitialLiveData(selectedSymbol, { publicMarketData: true }) : getStockInitialLiveData(selectedSymbol))
          : { snapshot: await (readOnlyMode ? getStockSnapshot(selectedSymbol, undefined, { publicMarketData: true }) : getStockSnapshot(selectedSymbol)), bundledBars: null };
        if (!active) return;
        setLiveSnapshot((current) => ({
          symbol: selectedSymbol,
          status: "ready",
          data: result.snapshot,
          bundledBars: includeBundle
            ? result.bundledBars
            : current.symbol === selectedSymbol
            ? current.bundledBars
            : null,
          error: "",
        }));
      } catch (error) {
        if (!active) return;
        setLiveSnapshot((current) => ({
          symbol: selectedSymbol,
          status: "error",
          data:
            preserveCurrent && current.symbol === selectedSymbol ? current.data : null,
          bundledBars:
            preserveCurrent && current.symbol === selectedSymbol
              ? current.bundledBars
              : null,
          error: error.message || "Authenticated Alpaca market data is unavailable.",
        }));
      }
    };

    loadSnapshot({ preserveCurrent: hasMatchingSeed, includeBundle: true });
    timerId = window.setInterval(
      () => loadSnapshot({ preserveCurrent: true }),
      LIVE_REFRESH_MS
    );

    return () => {
      active = false;
      if (timerId) window.clearInterval(timerId);
    };
  }, [isActive, isDemoActive, isScannerReview, scannerSeed, selectedSymbol]);

  const selectedLiveSnapshot =
    liveSnapshot.symbol === selectedSymbol
      ? liveSnapshot
      : {
          symbol: selectedSymbol,
          status: "loading",
          data: null,
          bundledBars: null,
          error: "",
        };

  if (scannerReview) {
    return <ScannerReviewWorkspace review={scannerReview} />;
  }

  const {
    market,
    marketMetrics,
    currentTrade,
    ninaVerdict,
    ninaIntelligence,
    liveReasoning,
    warningSignals = [],
    sessionMemory,
  } = ninaMarketState;

  const displayedMarket = isDemoActive
    ? {
        ...market,
        mood: demoMarketState.marketMood,
        bias: demoMarketState.marketBias,
        confidence: demoMarketState.confidence,
        riskLevel: demoMarketState.riskLevel,
      }
    : market;

  const displayedTrade = isDemoActive
    ? buildDemoTrade(currentTrade, selectedSymbol, demoMarketState)
    : liveTradeFromSnapshot(selectedLiveSnapshot.data, selectedSymbol, currentTrade);

  const displayedVerdict = isDemoActive
    ? {
        ...ninaVerdict,
        decision: demoMarketState.decision,
        action: demoMarketState.decision,
        tradePermission: demoMarketState.nextAction,
        explanation: demoMarketState.message,
      }
    : ninaVerdict;

  const displayedIntelligence = isDemoActive
    ? {
        ...ninaIntelligence,
        status:
          demoMarketState.simulationStatus === "Running"
            ? "Demo Running"
            : "Demo Paused",
        state: demoMarketState.trend,
        confidence: demoMarketState.confidence,
        recommendedAction: demoMarketState.nextAction,
      }
    : ninaIntelligence;

  const displayedReasoning = isDemoActive
    ? {
        ...liveReasoning,
        headline: `${demoMarketState.symbol}  -  ${demoMarketState.scenarioName}`,
        summary: demoMarketState.message,
        detailedExplanation: demoMarketState.message,
      }
    : liveReasoning;

  
  const displayedWarnings = isDemoActive ? createDemoWarnings(demoMarketState) : warningSignals;
  const displayedSessionMemory = isDemoActive
    ? createDemoSessionMemory(demoMarketState)
    : sessionMemory;

  const source = selectedLiveSnapshot.data?.source;
  const livePriceReady = !isDemoActive && finiteNumber(displayedTrade.currentPrice) !== null;
  const liveDataReady =
    !isDemoActive && selectedLiveSnapshot.status === "ready" && livePriceReady;
  const liveDataStale =
    !isDemoActive && selectedLiveSnapshot.status === "error" && livePriceReady;
  const liveDataBlocked = !isDemoActive && !liveDataReady;
  const liveDataLabel = source
    ? `${source.provider || "Alpaca"} ${(source.feed || "").toUpperCase()}`.trim()
    : "Alpaca";
const liveNinaDecision = analyzeLiveNinaDecision({
  trade: displayedTrade,
  dataStatus: selectedLiveSnapshot.status,
  isStale: liveDataStale,
});
const liveNinaDecisionText = String(
  liveNinaDecision?.decision || ""
).toUpperCase();

const liveNinaSignalColor = liveNinaDecisionText.includes("BEARISH")
  ? "red"
  : liveNinaDecisionText.includes("WATCH LONG SETUP") ||
    liveNinaDecisionText.includes("BULLISH")
  ? "green"
  : "yellow";
  const tradeIsBlocked =
  liveDataBlocked ||
  liveNinaDecision.status === "BLOCKED" ||
  liveNinaDecision.status === "DATA BLOCKED" ||
  displayedTrade.stage === "BLOCKED" ||
  displayedVerdict.decision === "Risk Too High" ||
  displayedVerdict.decision === "Do Not Enter";

  const positionStatus = tradeIsBlocked
    ? liveDataStale
      ? "STALE DATA - BLOCKED"
      : selectedLiveSnapshot.status === "seeded"
      ? "SCANNER SEED - REFRESHING"
      : liveDataBlocked
      ? "DATA UNAVAILABLE"
      : "NO POSITION"
    : displayedTrade.stage === "BUILDING"
    ? "ENTRY BUILDING"
    : displayedTrade.stage === "BREAKOUT WATCH"
    ? "WATCHING"
    : "MONITORING";

    
  const liveRisk = analyzeLiveRisk({
    trade: displayedTrade,
    liveDecision: liveNinaDecision,
    accountSize: liveAccountSize,
  });
  const plannedQuantity =
    plannedQuantityState.symbol === displayedTrade.symbol
      ? plannedQuantityState.quantity
      : liveRisk.recommendedShares;
  const smartFlowAnalysis = analyzeNinaSmartFlow(
    isDemoActive
      ? {
          mode: "demo",
          symbol: displayedTrade.symbol,
          currentPrice: displayedTrade.currentPrice,
          vwap: displayedTrade.vwap,
          ema20: displayedTrade.ema20,
          dayHigh: displayedTrade.dayHigh,
          dayLow: displayedTrade.dayLow,
          entry: displayedTrade.entry,
          stop: displayedTrade.stop,
          targetOne: displayedTrade.targets.target1,
          targetTwo: displayedTrade.targets.target2,
          confidence: demoMarketState.confidence,
          directionalEvidence: demoMarketState.trend,
          riskLevel: demoMarketState.riskLevel,
          volumeLabel: demoMarketState.volume,
          volumeEvidenceAvailable: true,
          tradeBlocked: tradeIsBlocked,
        }
      : {
          mode: "live",
          symbol: displayedTrade.symbol,
          currentPrice: displayedTrade.currentPrice,
          vwap: displayedTrade.vwap,
          ema20: displayedTrade.ema20,
          dayHigh: displayedTrade.dayHigh,
          dayLow: displayedTrade.dayLow,
          entry: displayedTrade.entry,
          stop: displayedTrade.stop,
          targetOne: displayedTrade.targets.target1,
          targetTwo: displayedTrade.targets.target2,
          confidence: liveNinaDecision.confidence,
          directionalEvidence: liveNinaDecision.decision,
          bullishSignals: liveNinaDecision.bullishSignals,
          bearishSignals: liveNinaDecision.bearishSignals,
          decisionStatus: liveNinaDecision.status,
          riskLevel: liveNinaDecision.risk,
          volumeEvidenceAvailable: false,
          liquidityAvailable: false,
          institutionalDataAvailable: false,
          levelTwoAvailable: false,
          timeAndSalesAvailable: false,
          tradeBlocked:
            liveDataBlocked ||
            liveNinaDecision.status === "BLOCKED" ||
            liveNinaDecision.status === "DATA BLOCKED",
        }
  );

  const activeWarning =
    displayedWarnings.find((warning) => warning.severity === "High") ||
    displayedWarnings.find((warning) => warning.severity === "Medium") ||
    displayedWarnings[0];

  if (readOnlyMode) {
    return (
      <section
        className="dashboardCard liveChartWorkspace"
        aria-label="Read-only live market chart"
        data-testid="read-only-live-chart"
      >
        <div className="chartWorkspaceHeader">
          <div>
            <span className="workspaceEyebrow">AUTHENTICATED MARKET DATA · READ ONLY</span>
            <h2>Live Market Chart</h2>
            <p>
              <span data-testid="live-chart-symbol">{displayedTrade.symbol}</span>
              {" "}· {liveDataLabel}
            </p>
          </div>
          <div className="workspaceLiveCluster">
            <span>
              {selectedLiveSnapshot.status === "loading"
                ? "LIVE DATA LOADING"
                : liveDataStale
                ? "STALE MARKET DATA"
                : liveDataReady
                ? "LIVE MARKET DATA"
                : "LIVE DATA UNAVAILABLE"}
            </span>
            <strong>READ ONLY · NO ORDERS</strong>
          </div>
        </div>

        {!isDemoActive && source?.nina_data_quality_notice && (
          <div className="workspaceWarning workspaceWarningMedium" role="status">
            <span>!</span>
            <div>
              <strong>Market Data Quality Notice</strong>
              <p>{source.nina_data_quality_notice}</p>
            </div>
          </div>
        )}

        {!isDemoActive && selectedLiveSnapshot.status === "error" && (
          <div className="workspaceWarning workspaceWarningHigh" role="alert">
            <span>!</span>
            <div>
              <strong>Live market data unavailable</strong>
              <p>{selectedLiveSnapshot.error} No simulated price has been substituted.</p>
            </div>
          </div>
        )}

        <div className="workspacePanel chartPanel centerCommandPanel">
          <LiveProfessionalChartWorkspace
            trade={{ ...displayedTrade, stage: liveNinaDecision.tradeStage }}
            market={displayedMarket}
            snapshot={isDemoActive ? null : selectedLiveSnapshot.data}
            bundledBars={isDemoActive ? null : selectedLiveSnapshot.bundledBars}
            dataStatus={isDemoActive ? "simulated" : selectedLiveSnapshot.status}
            isDemoActive={isDemoActive}
            allowAuthenticatedStream={false}
            publicMarketData={true}
            basicMode
          />
        </div>
      </section>
    );
  }

  return (
    <section className="dashboardCard liveChartWorkspace">
      {(
        <>
      <LiveDecisionReporter
        decision={liveNinaDecision}
        onChange={onLiveDecisionChange}
      />
      <LiveTradePlanReporter
  symbol={displayedTrade.symbol}
  currentPrice={displayedTrade.currentPrice}
  decision={liveNinaDecision.decision}
  confidence={liveNinaDecision.confidence}
  risk={liveNinaDecision.risk}
  status={liveNinaDecision.status}
  blocked={tradeIsBlocked}
  quantity={plannedQuantity}
  entry={liveRisk.entry}
  stop={liveRisk.stop}
  target1={liveRisk.target1}
  target2={liveRisk.target2}
  target3={liveRisk.target3}
  maximumAllowedLoss={liveRisk.maximumAllowedLoss}
  actualMaximumLoss={liveRisk.actualMaximumLoss}
  riskPerShare={liveRisk.riskPerShare}
  onChange={onTradePlanChange}
/>
        </>
      )}
      <div className="chartWorkspaceHeader">
        <div>
          <span className="workspaceEyebrow">
            {isDemoActive
              ? "Demo Trade Intelligence"
              : selectedLiveSnapshot.status === "seeded"
              ? "Authenticated Scanner Seed  |  Refreshing"
              : "Authenticated Market Data"}
          </span>
          <h2> Live Trading Workspace</h2>
          <p>
            <span data-testid="live-chart-symbol">{displayedTrade.symbol}</span>{" "}
             -  {isDemoActive ? "explicit simulator" : `${liveDataLabel} market-data input`}
          </p>
        </div>

        <div className="workspaceLiveCluster">
          <span>
             {isDemoActive
              ? demoMarketState.simulationStatus.toUpperCase()
              : selectedLiveSnapshot.status === "seeded"
              ? "SCANNER SEED  |  REFRESHING"
              : selectedLiveSnapshot.status === "loading"
              ? "LIVE DATA LOADING"
              : liveDataStale
              ? `STALE  |  ${(source?.feed || "ALPACA").toUpperCase()}`
              : liveDataReady
              ? `LIVE  |  ${(source?.feed || "ALPACA").toUpperCase()}`
              : "LIVE DATA UNAVAILABLE"}
          </span>
          <strong>
            {isDemoActive
              ? displayedIntelligence.status.toUpperCase()
              : source?.quality_level?.toUpperCase() || "AUTHENTICATED ALPACA"}
          </strong>
        </div>
      </div>

      {!isDemoActive && source?.nina_data_quality_notice && (
        <div className="workspaceWarning workspaceWarningMedium" role="status">
          <span>! </span>
          <div>
            <strong>AI Data Quality Notice</strong>
            <p>{source.nina_data_quality_notice}</p>
          </div>
        </div>
      )}

      {!isDemoActive && selectedLiveSnapshot.status === "error" && (
        <div className="workspaceWarning workspaceWarningHigh" role="alert">
          <span>! </span>
          <div>
            <strong>{liveDataStale ? "Live refresh failed - quote is stale" : "Live price unavailable"}</strong>
            <p>
              {selectedLiveSnapshot.error} No simulated price has been substituted.
              {liveDataStale ? " Trading is blocked until a fresh authenticated quote arrives." : ""}
            </p>
          </div>
        </div>
      )}

      <div className="workspaceCommandStrip livePositionStrip liveDesktopPositionStrip">
        <div><span>Symbol</span><strong>{displayedTrade.symbol}</strong></div>
        <div>
          <span>Current Price</span>
          <strong data-testid="live-trading-current-price">{formatPrice(displayedTrade.currentPrice)}</strong>
        </div>
        <div><span>Position Status</span><strong>{positionStatus}</strong></div>
        <div><span>Open P/L</span><strong>{isDemoActive ? "Simulated" : "No verified open position"}</strong></div>
      </div>

      <div className={`tradingWorkspaceGrid ${tradingCardOpen ? "tradingCardOpen" : "tradingCardClosed"}`}>
        {tradingCardOpen && (
        <div className="workspacePanel riskWorkspacePanel">
          <QuickTradeTicket
            symbol={displayedTrade.symbol}
            entry={displayedTrade.entry || 0}
            stop={displayedTrade.stop || 0}
            targetOne={displayedTrade.targets.target1 || 0}
            targetTwo={displayedTrade.targets.target2 || 0}
            recommendedShares={liveRisk.recommendedShares}
            confidence={liveNinaDecision.confidence}
            tradeBlocked={tradeIsBlocked}
            ninaSignalColor={liveNinaSignalColor}
            session={source?.quality_level || "Regular Market"}
            accountLabel="LIVE  |  Planning only"
            currentPrice={displayedTrade.currentPrice}
            bid={displayedTrade.bid}
            ask={displayedTrade.ask}
            volume={displayedTrade.volume}
            marketDataSource={
              source
                ? `${source.provider || "Alpaca"}  |  ${String(source.feed || "market").toUpperCase()}  |  ${source.quality_level || "Authenticated market data"}`
                : "Authenticated quote unavailable"
            }
            onQuantityChange={setPlannedQuantityState}
          />
          <RiskEngine
  demoMarketState={demoMarketState}
  trade={{ ...displayedTrade, stage: liveNinaDecision.tradeStage }}
  liveDecision={liveNinaDecision}
  variant="risk"
/>
        </div>
        )}

        <div className="workspacePanel chartPanel centerCommandPanel">
          <div className="tradingCardToggleRow">
            <button
              type="button"
              className="tradingCardToggleButton"
              onClick={() => setTradingCardOpen((open) => !open)}
              aria-expanded={tradingCardOpen}
            >
              {tradingCardOpen ? "Hide Trading Card" : "Open Trading Card"}
            </button>
          </div>
          {(
          
 <DecisionEngine
  decision={liveNinaDecision.decision}
  confidence={liveNinaDecision.confidence}
  risk={liveNinaDecision.risk}
  reason={liveNinaDecision.reason}
  nextTrigger={liveNinaDecision.nextTrigger}
  variant="command"
/>
          )}

          <LiveProfessionalChartWorkspace
            trade={{ ...displayedTrade, stage: liveNinaDecision.tradeStage }}
            market={displayedMarket}
            snapshot={isDemoActive ? null : selectedLiveSnapshot.data}
            bundledBars={isDemoActive ? null : selectedLiveSnapshot.bundledBars}
            dataStatus={isDemoActive ? "simulated" : selectedLiveSnapshot.status}
            isDemoActive={isDemoActive}
            basicMode={false}
          />

          {(
            <NinaSmartFlowPanel analysis={smartFlowAnalysis} />
          )}

          <details className="liveChartReadDetails">
            <summary>Open objective chart analysis</summary>
            <LiveChartRead
              symbol={displayedTrade.symbol}
              confidence={displayedIntelligence.confidence}
              trend={isDemoActive ? demoMarketState.trend : displayedIntelligence.state}
              priceStructure={displayedReasoning.headline}
              vwap={
                displayedTrade.vwap === null
                  ? "VWAP unavailable"
                  : displayedTrade.currentPrice >= displayedTrade.vwap
                  ? "Above VWAP"
                  : "Below VWAP"
              }
              ema20={
                displayedTrade.ema20 === null
                  ? "EMA 20 unavailable"
                  : displayedTrade.currentPrice >= displayedTrade.ema20
                  ? "Above EMA 20"
                  : "Below EMA 20"
              }
              macd={isDemoActive ? demoMarketState.macd : "Not connected"}
              volume={isDemoActive ? demoMarketState.volume : "Snapshot only"}
              support={formatPrice(displayedTrade.dayLow)}
              resistance={formatPrice(displayedTrade.dayHigh)}
              summary={displayedReasoning.detailedExplanation}
            />
          </details>
        </div>

        {(
        <div className="workspacePanel coachWorkspacePanel">
          <LiveCoach
  suggestedAction={liveNinaDecision.suggestedAction}
  avoid={
    liveNinaDecision.status === "BLOCKED"
      ? "Do not enter until the authenticated risk condition clears."
      : liveNinaDecision.decision === "WATCH LONG SETUP"
      ? "Do not chase. Require confirmation and continued strength above VWAP."
      : "Do not force a trade while authenticated evidence is mixed."
  }
  status={liveNinaDecision.status}
  grade={liveNinaDecision.grade}
  tradeStage={liveNinaDecision.tradeStage}
/>
            
          <PartialProfitStrategy
  demoMarketState={demoMarketState}
  trade={{ ...displayedTrade, stage: liveNinaDecision.tradeStage }}
  liveDecision={liveNinaDecision}
  plannedQuantity={plannedQuantity}
/>
        </div>
        )}
      </div>

      <details className="liveSupplementalPanel">
        <summary>Open session intelligence and safeguards</summary>
        <div className="workspaceIntelligenceGrid" style={{ gridTemplateColumns: "1fr" }}>
          <div className="workspaceIntelligencePanel">
            <div className="workspaceIntelligenceHeader">
              <div><span>SESSION MEMORY</span><h3>How conditions evolved</h3></div>
              <strong>{displayedSessionMemory.sessionStatus}</strong>
            </div>
            <div className="workspaceEventList">
              {displayedSessionMemory.importantEvents.slice(-4).map((event) => (
                <div className="workspaceEvent" key={`${event.time}-${event.event}`}>
                  <span>{event.time}</span><p>{event.event}</p><strong>{event.impact}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        {activeWarning && (
          <div className={`workspaceWarning workspaceWarning${activeWarning.severity}`}>
            <span>! </span>
            <div><strong>{activeWarning.title}</strong><p>{activeWarning.message}</p></div>
          </div>
        )}
        <AITradeChecklist />
      </details>
    </section>
  );
}

function buildDemoTrade(currentTrade, selectedSymbol, demoMarketState) {
  const price = demoMarketState.price;
  return {
    ...currentTrade,
    symbol: selectedSymbol,
    currentPrice: price,
    dayHigh: price + 0.12,
    dayLow: price - 0.14,
    vwap:
      demoMarketState.vwap === "Above VWAP"
        ? price - 0.05
        : demoMarketState.vwap === "Testing VWAP"
        ? price
        : price + 0.06,
    ema20: demoMarketState.marketMood === "Bullish" ? price - 0.08 : price + 0.08,
    entry: price + 0.03,
    stop: price - 0.1,
    targets: { target1: price + 0.2, target2: price + 0.4, target3: price + 0.6 },
    riskReward: "1:2",
    confidence: demoMarketState.confidence,
    stage: getDemoStage(demoMarketState.decision),
    direction:
      demoMarketState.marketBias === "Long"
        ? "LONG"
        : demoMarketState.marketMood === "Bearish"
        ? "DEFENSIVE"
        : "WAIT",
    grade: getDemoGrade(demoMarketState.confidence),
    tradeStatus: demoMarketState.decision,
    confirmationRequired: demoMarketState.decision !== "Entry Confirmed",
  };
}

function ScannerReviewWorkspace({ review }) {
  const hasMarketSnapshot = review.kind === "day";
  const snapshotRows = hasMarketSnapshot
    ? [
        ["Last", formatScannerReviewPrice(review.lastPrice)],
        ["Previous close", formatScannerReviewPrice(review.previousClose)],
        ["Open", formatScannerReviewPrice(review.openPrice)],
        ["Bid", formatScannerReviewPrice(review.bid)],
        ["Ask", formatScannerReviewPrice(review.ask)],
        ["Spread", formatScannerReviewPrice(review.spread)],
        ["Day low", formatScannerReviewPrice(review.dayLow)],
        ["Day high", formatScannerReviewPrice(review.dayHigh)],
        ["Volume", review.volume.toLocaleString("en-US")],
        ["RVOL", `${review.relativeVolume.toFixed(2)}x`],
      ]
    : [
        ["Scanner mode", review.kind === "premarket" ? "Premarket" : review.kind === "swing" ? "Swing" : "Options"],
        ["Setup", review.setup], ["Direction", review.direction], ["Risk", review.riskLevel],
        ["SmartFlow estimate", `${review.smartFlow}/100`], ["Confidence estimate", `${review.confidence}/100`],
        ["Evidence", review.evidenceStatus], ["Completeness", `${review.evidenceCompleteness}%`],
      ];

  return (
    <section className="dashboardCard liveChartWorkspace scannerChartReview">
      <div className="chartWorkspaceHeader">
        <div>
          <span className="workspaceEyebrow">{formatScannerMode(review.kind)} Scanner Review  |  Simulated</span>
          <h2> Live Trading Workspace</h2>
          <p><span data-testid="live-chart-symbol">{review.symbol}</span>{" "} -  validated simulated {formatScannerMode(review.kind)} Scanner context</p>
        </div>
        <div className="workspaceLiveCluster"><span> SIMULATED REVIEW</span><strong>REVIEW ONLY</strong></div>
      </div>

      <div className="workspaceCommandStrip livePositionStrip">
        <div><span>Symbol</span><strong>{review.symbol}</strong></div>
        <div><span>Current Price</span><strong>{hasMarketSnapshot ? formatScannerReviewPrice(review.lastPrice) : "Not supplied"}</strong></div>
        <div><span>Position Status</span><strong>REVIEW ONLY</strong></div>
        <div><span>Open P/L</span><strong>No Position</strong></div>
      </div>

      <div className="scannerReviewSnapshot" aria-label={`Validated simulated ${formatScannerMode(review.kind)} review for ${review.symbol}`}>
        {snapshotRows.map(([label, value]) => <div key={label}><span>{label}</span><strong>{value}</strong></div>)}
        <p>
          Source: {review.source}  |  As of: {new Date(review.asOf).toLocaleString()}
          {hasMarketSnapshot ? `  |  Updated: ${new Date(review.updatedAt).toLocaleString()}` : ""}
        </p>
      </div>

      <div className="scannerReviewChartTicket">
        <div className="workspacePanel chartPanel scannerChartUnavailable" role="status">
          <span className="workspaceEyebrow">{review.symbol}  |  SIMULATED REVIEW CONTEXT</span>
          <h3>Chart series unavailable</h3>
          <p>
            {hasMarketSnapshot
              ? "Full candle history, VWAP, EMA, and historical indicators were not supplied by this validated Scanner snapshot."
              : "No validated market-price series was supplied by this scanner shortcut."}{" "}
            No unrelated market data has been substituted.
          </p>
        </div>
        <div className="workspacePanel">
          <QuickTradeTicket
            symbol={review.symbol}
            confidence={review.confidence}
            tradeBlocked={review.riskLevel === "Critical"}
            session="Simulated Scanner review"
            scannerReview
          />
        </div>
      </div>
    </section>
  );
}

function formatScannerMode(kind) {
  return {
    day: "Day Trading",
    options: "Options",
    "options-engine": "Options Opportunity",
    "opportunity-center": "AI Opportunity Center",
    premarket: "Premarket",
    swing: "Swing",
  }[kind] || "Scanner";
}

function formatPrice(price) {
  return typeof price === "number" && Number.isFinite(price) ? `$${price.toFixed(2)}` : "-";
}

function formatScannerReviewPrice(price) {
  if (typeof price !== "number" || !Number.isFinite(price)) return "-";
  const normalized = Object.is(price, -0) || Math.abs(price) < 0.00005
    ? 0
    : Math.round((price + Number.EPSILON) * 10000) / 10000;
  const roundedToCents = Math.round((normalized + Number.EPSILON) * 100) / 100;
  const hasMeaningfulSubCentValue = Math.abs(normalized - roundedToCents) > 1e-9;
  return `$${normalized.toFixed(hasMeaningfulSubCentValue ? 4 : 2)}`;
}

function getDemoStage(decision = "") {
  if (decision === "Entry Building") return "BUILDING";
  if (decision === "Watch Breakout") return "BREAKOUT WATCH";
  if (decision === "Do Not Enter" || decision === "Risk Too High") return "BLOCKED";
  return "MONITORING";
}

function getDemoGrade(confidence) {
  if (confidence >= 90) return "A+";
  if (confidence >= 80) return "A";
  if (confidence >= 70) return "B";
  if (confidence >= 50) return "C";
  return "D";
}

function createDemoWarnings(demoState) {
  if (demoState.riskLevel === "Very High" || demoState.decision === "Risk Too High") {
    return [{
      title: "Capital Protection Activated",
      message: "Support is breaking and selling pressure is increasing. The AI Assistant has blocked the trade.",
      severity: "High",
    }];
  }
  if (demoState.riskLevel === "High" || demoState.decision === "Do Not Enter") {
    return [{
      title: "Failed Breakout Warning",
      message: "Momentum has weakened and the breakout did not hold. Wait for a new setup.",
      severity: "High",
    }];
  }
  if (demoState.riskLevel === "Moderate") {
    return [{
      title: "Confirmation Still Required",
      message: "The setup is developing, but The AI Assistant needs the next candle to confirm direction.",
      severity: "Medium",
    }];
  }
  return [];
}

function createDemoSessionMemory(demoState) {
  return {
    sessionStatus: demoState.simulationStatus === "Running" ? "SIMULATING" : "PAUSED",
    currentEvolution: demoState.message,
    importantEvents: [
      { time: "09:31", event: `${demoState.symbol} entered the active scan.`, impact: "Monitoring" },
      { time: "09:33", event: `Volume changed to ${demoState.volume}.`, impact: demoState.volume },
      { time: "09:35", event: `MACD changed to ${demoState.macd}.`, impact: demoState.macd },
      { time: "09:37", event: `AI decision: ${demoState.decision}.`, impact: demoState.nextAction },
    ],
  };
}

export default LiveChartWorkspace;
